import { useState } from 'react';
import { DollarSign, Info, Edit2, Check, X, Lock } from 'lucide-react';
import { Task, Epic, Project, User } from '../types';
import { getEffectiveCostPerHour, getCostPerHourSource, formatCurrencyDetailed, DEFAULT_COST_PER_HOUR } from '../utils/costUtils';
import { canViewCost, canEditCost } from '../utils/permissions';
import { canEditProjectFinance, canViewProjectFinance } from '../utils/projectAccess';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { APP_NATIVE_FIELD_CLASS } from '../constants/formControls';
import {
  WIDGET_ALERT_DANGER_CLASS,
  WIDGET_ALERT_INFO_CLASS,
  WIDGET_ALERT_INFO_TEXT_CLASS,
  WIDGET_MUTED_TEXT_CLASS,
  WIDGET_STATUS_BADGE_DANGER_CLASS,
  WIDGET_STATUS_BADGE_INFO_CLASS,
  WIDGET_STATUS_BADGE_PURPLE_CLASS,
  WIDGET_STATUS_BADGE_SUCCESS_CLASS,
  WIDGET_SUBTLE_PANEL_CLASS,
} from '../constants/widgetTheme';

interface CostPerHourDisplayProps {
  task?: Task;
  epic?: Epic | null;
  project?: Project | null;
  currentUser?: User | null;
  onUpdateTask?: (updates: Partial<Task>) => void;
  onUpdateEpic?: (updates: Partial<Epic>) => void;
  onUpdateProject?: (updates: Partial<Project>) => void;
  readonly?: boolean;
  showDetails?: boolean;
}

export default function CostPerHourDisplay({
  task,
  epic,
  project,
  currentUser,
  onUpdateTask,
  onUpdateEpic,
  onUpdateProject,
  readonly = false,
  showDetails = true,
}: CostPerHourDisplayProps) {
  const [editingLevel, setEditingLevel] = useState<'task' | 'epic' | 'project' | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  // Check permissions
  const hasViewPermission = project
    ? canViewProjectFinance(currentUser || null, project)
    : canViewCost(currentUser || null);
  const hasEditPermission = project
    ? canEditProjectFinance(currentUser || null, project)
    : canEditCost(currentUser || null);

  // If user doesn't have permission to view cost, show locked message
  if (!hasViewPermission) {
    return (
      <div className={`${WIDGET_SUBTLE_PANEL_CLASS} p-4`}>
        <div className={`flex items-center gap-2 ${WIDGET_MUTED_TEXT_CLASS}`}>
          <Lock className="h-4 w-4" />
          <span className="text-sm">Cost information is restricted</span>
        </div>
      </div>
    );
  }

  const effectiveCostPerHour = task 
    ? getEffectiveCostPerHour(task, epic, project)
    : epic
    ? (epic.costPerHour || project?.costPerHour || DEFAULT_COST_PER_HOUR)
    : (project?.costPerHour || DEFAULT_COST_PER_HOUR);

  const source = task ? getCostPerHourSource(task, epic, project) : 
                 epic ? (epic.costPerHour ? 'epic' : (project?.costPerHour ? 'project' : 'default')) :
                 (project?.costPerHour ? 'project' : 'default');

  const getSourceLabel = () => {
    switch (source) {
      case 'task': return 'Task-specific rate';
      case 'epic': return 'Epic rate';
      case 'project': return 'Project rate';
      case 'default': return 'Default system rate';
    }
  };

  const getSourceColor = () => {
    switch (source) {
      case 'task': return WIDGET_STATUS_BADGE_INFO_CLASS;
      case 'epic': return WIDGET_STATUS_BADGE_PURPLE_CLASS;
      case 'project': return WIDGET_STATUS_BADGE_SUCCESS_CLASS;
      case 'default': return WIDGET_STATUS_BADGE_DANGER_CLASS;
    }
  };

  const startEdit = (level: 'task' | 'epic' | 'project') => {
    const currentValue = 
      level === 'task' ? task?.costPerHour :
      level === 'epic' ? epic?.costPerHour :
      project?.costPerHour;
    
    setEditValue(currentValue?.toString() || '');
    setEditingLevel(level);
  };

  const cancelEdit = () => {
    setEditingLevel(null);
    setEditValue('');
  };

  const saveEdit = () => {
    const value = editValue === '' ? undefined : parseFloat(editValue);
    
    if (value !== undefined && (isNaN(value) || value < 0)) {
      alert('Please enter a valid positive number or leave blank to inherit from parent');
      return;
    }

    if (editingLevel === 'task' && onUpdateTask) {
      onUpdateTask({ costPerHour: value });
    } else if (editingLevel === 'epic' && onUpdateEpic) {
      onUpdateEpic({ costPerHour: value });
    } else if (editingLevel === 'project' && onUpdateProject) {
      onUpdateProject({ costPerHour: value });
    }

    setEditingLevel(null);
    setEditValue('');
  };

  const renderEditableRow = (
    level: 'task' | 'epic' | 'project',
    label: string,
    currentValue: number | undefined,
    canEdit: boolean
  ) => {
    const isEditing = editingLevel === level;
    const hasValue = currentValue !== undefined && currentValue !== null;

    return (
      <div className="flex items-center justify-between py-2 border-b last:border-b-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-card-foreground">{label}</span>
          {!hasValue && (
            <Badge variant="outline" className="text-xs">
              Not set
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <div className="flex items-center gap-1">
                <span className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>$</span>
                <Input
                  type="number"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder="Inherit"
                  className={`${APP_NATIVE_FIELD_CLASS} h-8 w-24 text-sm`}
                  min="0"
                  step="0.01"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveEdit();
                    if (e.key === 'Escape') cancelEdit();
                  }}
                />
                <span className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>/hr</span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                onClick={saveEdit}
              >
                <Check className="h-4 w-4 text-chart-2" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                onClick={cancelEdit}
              >
                <X className="h-4 w-4 text-destructive" />
              </Button>
            </>
          ) : (
            <>
              <span className="text-sm font-semibold text-card-foreground">
                {hasValue ? formatCurrencyDetailed(currentValue) : 'Not set'}/hr
              </span>
              {canEdit && !readonly && hasEditPermission && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  onClick={() => startEdit(level)}
                >
                  <Edit2 className={`h-3.5 w-3.5 ${WIDGET_MUTED_TEXT_CLASS}`} />
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  if (!showDetails) {
    return (
      <div className="flex items-center gap-2">
        <DollarSign className={`h-4 w-4 ${WIDGET_MUTED_TEXT_CLASS}`} />
        <span className="text-sm font-semibold text-card-foreground">
          {formatCurrencyDetailed(effectiveCostPerHour)}/hr
        </span>
        <Badge variant="secondary" className={`text-xs ${getSourceColor()}`}>
          {getSourceLabel()}
        </Badge>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Current effective rate */}
      <div className={`${WIDGET_SUBTLE_PANEL_CLASS} p-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className={`h-5 w-5 ${WIDGET_MUTED_TEXT_CLASS}`} />
            <div>
              <div className={`text-sm font-medium ${WIDGET_MUTED_TEXT_CLASS}`}>Effective Cost Per Hour</div>
              <div className="text-2xl font-bold text-card-foreground">
                {formatCurrencyDetailed(effectiveCostPerHour)}
                <span className={`text-sm font-normal ${WIDGET_MUTED_TEXT_CLASS}`}>/hr</span>
              </div>
            </div>
          </div>
          <Badge className={getSourceColor()}>
            {getSourceLabel()}
          </Badge>
        </div>
        
        {source !== 'task' && (
                  <div className={`mt-3 flex items-start gap-2 rounded p-2 text-xs ${WIDGET_ALERT_INFO_CLASS} ${WIDGET_ALERT_INFO_TEXT_CLASS}`}>
                    <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <span>
              This rate is inherited from the {source === 'epic' ? 'epic' : source === 'project' ? 'project' : 'system default'}.
              {task && ' Set a task-specific rate to override.'}
            </span>
          </div>
        )}
      </div>

      {/* Hierarchy display and editing */}
      <div className="overflow-hidden rounded-lg border border-border">
        <div className="border-border bg-muted/60 px-4 py-2">
          <h4 className="text-sm font-semibold text-card-foreground">Cost Per Hour Hierarchy</h4>
        </div>
        <div className="p-4 space-y-1">
          {task && renderEditableRow('task', 'Task Level', task.costPerHour, !!onUpdateTask)}
          {epic && renderEditableRow('epic', 'Epic Level', epic.costPerHour, !!onUpdateEpic)}
          {project && renderEditableRow('project', 'Project Level', project.costPerHour, !!onUpdateProject)}
          
          <div className="mt-2 flex items-center justify-between rounded bg-muted/40 px-3 py-2">
            <span className="text-sm font-medium text-card-foreground">System Default</span>
            <span className="text-sm font-semibold text-card-foreground">
              {formatCurrencyDetailed(DEFAULT_COST_PER_HOUR)}/hr
            </span>
          </div>
        </div>
      </div>

      {!readonly && (
        <div className={`text-xs italic ${WIDGET_MUTED_TEXT_CLASS}`}>
          💡 Tip: Leave a level blank to inherit from the next level up. The hierarchy is: Task → Epic → Project → System Default.
        </div>
      )}
    </div>
  );
}